import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, ArrowUp, ArrowDown, ArrowUpDown, Pencil, X, AlertCircle, Plus, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { expensasApi } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';

const fmtPct = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Foresta es el único edificio que utiliza el criterio "Locales".
// En Torre Emilia esta columna no se muestra y se omite del payload (queda en 0).
const FORESTA_ID = 'edif_6034e848d0ed';

const COLS_BASE = [
  { key: 'codigo', label: 'Código', align: 'left', type: 'string', sortable: true },
  { key: 'cuenta', label: 'Cuenta', align: 'left', type: 'string', sortable: true },
  { key: 'titulo', label: 'Grupo', align: 'left', type: 'string', sortable: true },
  { key: 'rubro', label: 'Rubro', align: 'left', type: 'string', sortable: true },
  { key: 'subrubro', label: 'Subrubro', align: 'left', type: 'string', sortable: true },
  { key: 'part_expensas', label: '% Edificio', align: 'right', type: 'number', sortable: false },
  { key: 'part_cocheras', label: '% Cocheras', align: 'right', type: 'number', sortable: false },
];
const COL_LOCALES = { key: 'part_locales', label: '% Locales', align: 'right', type: 'number', sortable: false };
const COLS_TAIL = [
  { key: 'suma', label: 'Suma', align: 'right', type: 'number', sortable: false },
  { key: 'acciones', label: '', align: 'right', type: 'string', sortable: false },
];

export default function TabPlanCuentas({ edificioId, registerHeader }) {
  const showLocales = edificioId === FORESTA_ID;
  const COLS = useMemo(
    () => (showLocales ? [...COLS_BASE, COL_LOCALES, ...COLS_TAIL] : [...COLS_BASE, ...COLS_TAIL]),
    [showLocales]
  );
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [original, setOriginal] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState('codigo');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await expensasApi.listPlanCuentas(edificioId);
      setCuentas(r.data || []);
      setOriginal(JSON.parse(JSON.stringify(r.data || [])));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [edificioId]);

  const update = (idx, field, value) => {
    const next = [...cuentas];
    next[idx] = { ...next[idx], [field]: value };
    setCuentas(next);
  };

  const addRow = () => {
    setCuentas([
      { codigo: '', cuenta: '', titulo: '', rubro: '', subrubro: '',
        part_expensas: 1, part_adm_renta: 0, part_cocheras: 0, part_locales: 0, _isNew: true },
      ...cuentas,
    ]);
  };

  const deleteRow = (idx) => setCuentas(cuentas.filter((_, i) => i !== idx));

  const handleCancel = () => {
    setCuentas(JSON.parse(JSON.stringify(original)));
    setEditing(false);
  };

  // Suma de participaciones para validar = 1 (compat: incluye locales solo en Foresta)
  const sumaParticip = (c) =>
    Number(c.part_expensas || 0) + Number(c.part_cocheras || 0) +
    (showLocales ? Number(c.part_locales || 0) : 0);

  const handleSave = async () => {
    const codigos = cuentas.map((c) => String(c.codigo || '').trim());
    const vacios = codigos.filter((c) => !c).length;
    if (vacios > 0) { toast.error(`${vacios} cuenta(s) sin código`); return; }
    const dup = codigos.find((c, i) => codigos.indexOf(c) !== i);
    if (dup) { toast.error(`Código duplicado: ${dup}`); return; }
    const malas = cuentas.filter((c) => Math.abs(sumaParticip(c) - 1) > 0.001);
    if (malas.length) {
      const cols = showLocales ? '%Edificio + %Cocheras + %Locales' : '%Edificio + %Cocheras';
      toast.error(`${malas.length} cuentas: ${cols} debe sumar 1`);
      return;
    }
    setSaving(true);
    try {
      await expensasApi.bulkPlan(edificioId, cuentas.map((c) => ({
        codigo: String(c.codigo).trim(),
        cuenta: c.cuenta, titulo: c.titulo, rubro: c.rubro, subrubro: c.subrubro,
        part_expensas: Number(c.part_expensas || 0),
        part_adm_renta: 0,
        part_cocheras: Number(c.part_cocheras || 0),
        // En Torre Emilia siempre 0 (no se muestra ni se edita).
        part_locales: showLocales ? Number(c.part_locales || 0) : 0,
      })));
      toast.success('Plan de cuentas guardado');
      setEditing(false);
      await fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => cuentas.filter((c) =>
    !filter || `${c.codigo} ${c.cuenta} ${c.titulo} ${c.rubro} ${c.subrubro}`.toLowerCase().includes(filter.toLowerCase())
  ), [cuentas, filter]);

  // ─── Copia desde otro edificio ─────────────────────────────────────────────
  const TORRE_EMILIA_ID = 'edif_74b24adaccb6';
  const [copying, setCopying] = useState(false);
  const handleCopyFromTorreEmilia = async () => {
    if (edificioId === TORRE_EMILIA_ID) return;
    const hayExistentes = cuentas.length > 0;
    const msg = hayExistentes
      ? `Foresta ya tiene ${cuentas.length} cuenta(s) cargadas. ¿Querés copiar las que faltan desde Torre Emilia? (Las cuentas con código duplicado NO se sobreescribirán)`
      : '¿Copiar el plan de cuentas completo desde Torre Emilia? Foresta tendrá su propia copia editable.';
    if (!window.confirm(msg)) return;
    setCopying(true);
    try {
      const res = await expensasApi.copyPlanFrom(edificioId, TORRE_EMILIA_ID, false);
      const { creadas, ya_existian, destino, suma_porcentajes_origen, suma_porcentajes_destino, match_porcentajes } = res.data || {};
      let toastMsg = `${creadas} cuentas creadas en ${destino?.nombre || 'destino'}`;
      if (ya_existian > 0) toastMsg += ` · ${ya_existian} ya existían (sin cambios)`;
      toast.success(toastMsg);
      if (!match_porcentajes) {
        toast.warning(
          `Atención: suma % difiere. Origen exp=${suma_porcentajes_origen?.part_expensas} coch=${suma_porcentajes_origen?.part_cocheras} | Destino exp=${suma_porcentajes_destino?.part_expensas} coch=${suma_porcentajes_destino?.part_cocheras}`
        );
      }
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al copiar plan de cuentas');
    } finally {
      setCopying(false);
    }
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const col = COLS.find((c) => c.key === sortKey);
    if (!col) return arr;
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === 'suma') {
        va = Number(a.part_expensas || 0) + Number(a.part_cocheras || 0) + (showLocales ? Number(a.part_locales || 0) : 0);
        vb = Number(b.part_expensas || 0) + Number(b.part_cocheras || 0) + (showLocales ? Number(b.part_locales || 0) : 0);
      } else { va = a[sortKey]; vb = b[sortKey]; }
      if (col.type === 'number') return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
      return String(va || '').localeCompare(String(vb || ''), 'es', { numeric: true }) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir, COLS, showLocales]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  useEffect(() => {
    if (!registerHeader) return;
    registerHeader(
      <>
        <Input placeholder="Buscar código, cuenta, rubro..." value={filter}
               onChange={(e) => setFilter(e.target.value)}
               className="h-9 max-w-xs" data-testid="plan-filter" />
        {editing ? (
          <>
            <Button size="sm" variant="outline" onClick={addRow} disabled={saving} data-testid="add-cuenta-btn">
              <Plus className="w-4 h-4 mr-1" /> Nueva cuenta
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel} disabled={saving} data-testid="cancelar-plan-btn">
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="guardar-plan-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </>
        ) : (
          <>
            {edificioId !== TORRE_EMILIA_ID && (
              <Button
                size="sm"
                variant={cuentas.length === 0 ? 'default' : 'outline'}
                onClick={handleCopyFromTorreEmilia}
                disabled={copying}
                data-testid="copiar-plan-te-btn"
                title="Copiar plan de cuentas desde Torre Emilia"
              >
                {copying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
                Copiar desde Torre Emilia
              </Button>
            )}
            <Button size="sm" onClick={() => setEditing(true)} data-testid="editar-plan-btn">
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
          </>
        )}
      </>
    );
    return () => registerHeader(null);
    // eslint-disable-next-line
  }, [filter, cuentas, saving, editing, copying]);

  const erroresSuma = useMemo(() =>
    cuentas.filter((c) => Math.abs(sumaParticip(c) - 1) > 0.001).length
  , [cuentas, showLocales]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown className="w-3 h-3 text-slate-300 inline-block ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-indigo-600 inline-block ml-1" />
      : <ArrowDown className="w-3 h-3 text-indigo-600 inline-block ml-1" />;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-130px)] overflow-y-auto">
      {erroresSuma > 0 && (
        <div className="sticky top-0 z-20 bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span><b>{erroresSuma}</b> {erroresSuma === 1 ? 'cuenta tiene' : 'cuentas tienen'}{' '}
            {showLocales ? '%Edificio + %Cocheras + %Locales' : '%Edificio + %Cocheras'} que no suma 1 (100%). Las filas en rojo necesitan corrección.</span>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0 z-10">
          <tr>
            {COLS.map((col) => (
              <th key={col.key}
                  className={`py-2 px-3 font-medium select-none whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-slate-100' : ''} ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                  data-testid={col.sortable ? `sort-${col.key}` : undefined}>
                {col.label}
                {col.sortable && <SortIcon k={col.key} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sorted.map((c) => {
            const idx = cuentas.indexOf(c);
            const suma = sumaParticip(c);
            const ok = Math.abs(suma - 1) < 0.001;
            const rowKey = c._isNew ? `new-${idx}` : c.codigo;
            return (
              <tr key={rowKey} className={ok ? 'hover:bg-slate-50/60' : 'bg-red-50/60 hover:bg-red-50'}>
                <td className="py-1 px-3 font-mono text-slate-600 text-xs">
                  {!ok && <AlertCircle className="w-3.5 h-3.5 text-red-500 inline-block mr-1 -mt-0.5" />}
                  {editing ? (
                    <Input className="h-7 text-xs font-mono w-24" value={c.codigo ?? ''}
                           placeholder="Código"
                           onChange={(e) => update(idx, 'codigo', e.target.value)} />
                  ) : c.codigo}
                </td>
                <td className="py-1 px-3 text-slate-900">
                  {editing ? (
                    <Input className="h-7 text-sm" value={c.cuenta ?? ''}
                           onChange={(e) => update(idx, 'cuenta', e.target.value)}
                           data-testid={`cuenta-${c.codigo}`} />
                  ) : c.cuenta}
                </td>
                <td className="py-1 px-3 text-slate-600 text-xs">
                  {editing ? (
                    <Input className="h-7 text-xs" value={c.titulo ?? ''}
                           onChange={(e) => update(idx, 'titulo', e.target.value)} />
                  ) : (c.titulo || '—')}
                </td>
                <td className="py-1 px-3 text-slate-600 text-xs">
                  {editing ? (
                    <Input className="h-7 text-xs" value={c.rubro ?? ''}
                           onChange={(e) => update(idx, 'rubro', e.target.value)} />
                  ) : (c.rubro || '—')}
                </td>
                <td className="py-1 px-3 text-slate-600 text-xs">
                  {editing ? (
                    <Input className="h-7 text-xs" value={c.subrubro ?? ''}
                           onChange={(e) => update(idx, 'subrubro', e.target.value)} />
                  ) : (c.subrubro || '—')}
                </td>
                <td className="py-1 px-3">
                  <Input type="number" step="0.01" className="h-7 text-right w-20 ml-auto"
                         value={c.part_expensas ?? 0} disabled={!editing}
                         onChange={(e) => update(idx, 'part_expensas', e.target.value)} />
                </td>
                <td className="py-1 px-3">
                  <Input type="number" step="0.01" className="h-7 text-right w-20 ml-auto"
                         value={c.part_cocheras ?? 0} disabled={!editing}
                         onChange={(e) => update(idx, 'part_cocheras', e.target.value)} />
                </td>
                {showLocales && (
                  <td className="py-1 px-3">
                    <Input type="number" step="0.01" className="h-7 text-right w-20 ml-auto"
                           value={c.part_locales ?? 0} disabled={!editing}
                           data-testid={`part-locales-${c.codigo || idx}`}
                           onChange={(e) => update(idx, 'part_locales', e.target.value)} />
                  </td>
                )}
                <td className={`py-1 px-3 text-right font-mono text-xs font-semibold ${ok ? 'text-emerald-600' : 'text-red-600'}`}>
                  {fmtPct(suma)}
                </td>
                <td className="py-1 px-2 text-right">
                  {editing && (
                    <button type="button" onClick={() => deleteRow(idx)}
                            className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                            title="Eliminar cuenta"
                            data-testid={`delete-cuenta-${c.codigo || idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
